'use client';

import { useMemo, useState, useEffect } from 'react';
import { useRouter as usePageCompactRouter } from 'next/compat/router';
import {
  usePathname as useAppPathname,
  useRouter as useAppRouter,
  useParams,
  useSearchParams,
  type ReadonlyURLSearchParams,
} from 'next/navigation';
import { interceptNavigation } from './helpers/navigationGuardRegistry';
import { callAppRouterNav } from './helpers/callAppRouterNav';

export interface INavigateOptions {
  scroll?: boolean,
  /** Only applies to the Pages Router; drop it once this tenant moves to appRouter. */
  shallow?: boolean,
}

export type TRouter = {
  push: (href: string, options?: INavigateOptions) => void,
  replace: (href: string, options?: INavigateOptions) => void,
  back: () => void,
  forward: () => void,
  refresh: () => void,
  prefetch: (href: string) => void,
};

export type { ReadonlyURLSearchParams };

// These hooks are supported in the page router.
export { useParams, useSearchParams };

export const usePathname = (): string => {
  const pageRouter = usePageCompactRouter();
  const appPathname = useAppPathname();

  if (pageRouter) {
    return (pageRouter.asPath ?? '/').split('?')[0].split('#')[0];
  }

  return appPathname;
};

export const useRouter = (): TRouter => {
  const pageRouter = usePageCompactRouter();
  const appRouter = useAppRouter();

  return useMemo(() => {
    if (!pageRouter) {
      return {
        ...appRouter,
        // Lets a NextNavigationPrompt guard pause programmatic push/replace, not just <a>/<Link> clicks.
        push: (href: string, options?: INavigateOptions): void => {
          if (!interceptNavigation(href, false)) callAppRouterNav(appRouter.push, href, options);
        },
        replace: (href: string, options?: INavigateOptions): void => {
          if (!interceptNavigation(href, true)) callAppRouterNav(appRouter.replace, href, options);
        },
      };
    }

    return {
      push: (href: string, options?: INavigateOptions) => {
        pageRouter.push(href, undefined, { scroll: options?.scroll, shallow: options?.shallow });
      },
      replace: (href: string, options?: INavigateOptions) => {
        pageRouter.replace(href, undefined, { scroll: options?.scroll, shallow: options?.shallow });
      },
      back: () => pageRouter.back(),
      forward: () => {
        if (typeof window !== 'undefined') {
          window.history.forward();
        }
      },
      refresh: () => pageRouter.reload(),
      prefetch: (href: string) => {
        pageRouter.prefetch(href);
      },
    };
  }, [pageRouter, appRouter]);
};

export const useHash = (): string => {
  const pageRouter = usePageCompactRouter();
  const [hash, setHash] = useState('');

  useEffect(() => {
    const onHashChange = (): void => setHash(window.location.hash.slice(1));

    onHashChange();

    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  useEffect(() => {
    if (pageRouter?.asPath) {
      setHash(pageRouter.asPath.split('#')[1] || '');
    }
  }, [pageRouter?.asPath]);

  return hash;
};
